import isNumber from 'lodash/isNumber'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { AgGridReact } from 'ag-grid-react'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'

import config from '@/config'
import lang from '@/lang'
import { Title } from '@/modules/base/components/texts/index'
import { ClientSideRowModelModule } from 'ag-grid-community'
import { MenuModule, ClipboardModule } from 'ag-grid-enterprise'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import moment from 'moment'
import SimplePagination from '../list/pagination/simple-pagination'
import { ROW_SELECTION_MULTIPLE, ROW_SELECTION_SINGLE } from '../list/list-v2'
import formatAmount from '@/modules/_helpers/formatAmount'

const useStyles = makeStyles((theme) => ({
  container: ({ showBorder }) => ({
    paddingBottom: 1, // else borderRadius displayed wrong
    width: '100%',
    background: theme.white,
    border: showBorder ? theme.border : 'none',
    borderRadius: theme.borderRadius,
    display: 'flex',
    flexDirection: 'column',
  }),
  narrowListContainer: {
    width: '100%',
    border: 'none',
    padding: 'unset',
    margin: 'unset',
  },
  childrenWrapper: {
    paddingLeft: 17,
  },
  title: {
    fontSize: 16,
    paddingTop: 20,
    paddingLeft: 17,
    color: theme.black,
    fontWeight: theme.fontWeights.medium,
  },
  table: ({ showBorder }) => ({
    '& .ag-layout-normal.ag-root-wrapper-body': {
      height: 'auto',
      width: '100%',
    },
    '& .ag-layout-normal.ag-body': {
      height: 'auto',
    },
    '& .ag-row-last': {
      borderBottom: showBorder && 'none !important',
    },
    '& .ag-header-cell': {
      display: 'flex',
      alignItems: 'center',
    },
  }),
  pager: {
    width: '100%',
    marginTop: 'auto',
  },
}))

const getValueFormatter = (type) => {
  switch (type) {
    case 'number':
      return { valueFormatter: ({ value }) => isNumber(value) ? formatCurrency(value) : lang.generics.emptyProp }
    case 'currency':
      return { valueFormatter: ({ value }) => value ? formatCurrency(value) : lang.generics.emptyProp }
    case 'amount':
      return { valueFormatter: ({ value }) => value ? formatAmount(value) : lang.generics.emptyProp }
    case 'date':
      return { valueFormatter: ({ value }) => value && moment(value).isValid() ? moment(value).format(config.dateFormat.client) : lang.generics.emptyProp }
    default:
      return { valueFormatter: ({ value }) => value ?? lang.generics.emptyProp }
  }
}

const getColumns = (columns) => {
  return columns.map((column) => ({
    ...(getValueFormatter(column.fieldType)),
    ...column,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
    menuTabs: [],
  }))
}

const modules = [ClientSideRowModelModule, MenuModule, ClipboardModule]

const SimpleAgGrid = (props) => {
  const [grid, setGrid] = useState(null)
  const gridRef = useRef(null)
  const {
    title,
    data,
    columns,
    isNarrowList = false,
    className,
    onRowClicked = () => {},
    onRowDataUpdated,
    children,
    pagination = false,
    paginationPageSize = 10,
    manualPagination = false,
    currentPage,
    totalPages,
    onPageChange,
    onSortChanged,
    localeText = {},
    defaultColDef = {},
    autoFitColumns = true,
    rowSelection = ROW_SELECTION_SINGLE,
    loading,
    suppressRowClickSelection = false,
    showBorder = true,
    ...restGridSettings
  } = props
  const classes = useStyles({ showBorder })

  const getValidGrid = () => {
    return grid?.api.destroyCalled === false ? grid : null
  }

  useEffect(() => {
    // needs to be set to get the correct with of the table after component did mount
    autoFitColumns && window.setTimeout(onWindowResize, 300)
    autoFitColumns && window.addEventListener('resize', onWindowResize)
    return () => {
      autoFitColumns && window.removeEventListener('resize', onWindowResize)
    }
  }, [])

  useEffect(() => {
    autoFitColumns && getValidGrid()?.api?.sizeColumnsToFit()
  }, [props])

  const onGridReady = (grid) => {
    setGrid(grid)
    autoFitColumns && grid.api.sizeColumnsToFit()
    if (gridRef.current && props.onGridReady) props.onGridReady(gridRef.current)
  }

  useEffect(() => {
    if (grid) {
      if (loading) {
        grid.api.showLoadingOverlay()
      } else {
        grid.api.hideOverlay()
      }
    }
  }, [grid, loading])

  const onWindowResize = () => {
    autoFitColumns && getValidGrid()?.api?.sizeColumnsToFit()
  }

  const getRightClickContextMenuItems = (params) => {
    return !props.disableRightClickContextMenu && params.value ? [props.customRightClickContextMenu || 'copy'] : []
  }

  const handleSortChanged = (event) => {
    if (!onSortChanged) return
    const sortedColumn = event.columnApi.getAllGridColumns().find((column) => column.sort)
    onSortChanged(sortedColumn ? { field: sortedColumn.colDef.field, direction: sortedColumn.sort } : null)
  }

  const processCellForClipboard = (params) => {
    const { value, column: { colId }, node: { data } } = params
    const relatedColumn = columns.find(col => col.field === colId)
    if (relatedColumn) {
      switch (relatedColumn.fieldType) {
        case 'date':
          return moment(data[colId] || value).isValid() ? moment(data[colId] || value).format(config.dateFormat.client) : lang.generics.emptyProp

        case 'number':
          return isNumber(data[colId] || value) ? formatCurrency(data[colId] || value) : lang.generics.emptyProp

        default:
          return data[colId] || value
      }
    }
    return data[colId] || value
  }

  const columnDefs = useMemo(() => {
    const cols = getColumns(columns)
    // with manualPagination, rowData only ever holds the current server-fetched page, already
    // ordered by the server — a no-op comparator keeps the sort UI/events working without
    // ag-grid re-sorting that (potentially display-formatted) page data itself
    return manualPagination ? cols.map((col) => ({ ...col, comparator: () => 0 })) : cols
  }, [columns, manualPagination])

  const defaultColDefMemo = useMemo(() => ({
    sortable: false,
    resizable: false,
    ...defaultColDef,
  }), [defaultColDef])

  const localeTextMemo = useMemo(() => ({
    ...lang.list,
    noRowsToShow: lang.invoiceDetail.noConnectedTransaction,
    ctrlC: ' ',
    ...localeText,
  }), [localeText])

  return (
    <div className={clsx(classes.container, isNarrowList && classes.narrowListContainer, className)}>
      {title && <Title className={classes.title}>{title}</Title>}
      {children && (
        <div className={classes.childrenWrapper}>
          {children}
        </div>
      )}
      {/* // just for testing purposes
      <div>
        <button onClick={() => gridRef.current?.api?.sizeColumnsToFit()}>sizeColumnsToFit</button>
        <button onClick={() => gridRef.current?.api?.columnModel.autoSizeAllColumns()}>autoSizeAllColumns</button>
      </div> */}
      <div className={clsx('ag-theme-material', classes.table)}>
        <AgGridReact
          ref={gridRef}
          modules={modules}
          rowData={data}
          columnDefs={columnDefs}
          defaultColDef={defaultColDefMemo}
          enableColResize={false}
          enableSorting={false}
          rowSelection={rowSelection}
          suppressRowClickSelection={suppressRowClickSelection}
          suppressPropertyNamesCheck={true}
          suppressDragLeaveHidesColumns={true}
          enableServerSideSorting={false}
          localeText={localeTextMemo}
          onRowDataUpdated={onRowDataUpdated}
          onRowClicked={onRowClicked}
          getContextMenuItems={getRightClickContextMenuItems}
          processCellForClipboard={processCellForClipboard}
          pagination={pagination && !manualPagination}
          paginationPageSize={paginationPageSize}
          suppressPaginationPanel={true}
          loading={loading}
          {...restGridSettings}
          onGridReady={onGridReady}
          onSortChanged={handleSortChanged}
        />
      </div>
      {(pagination || manualPagination) && (
        <SimplePagination
          className={classes.pager}
          gridApi={getValidGrid()?.api}
          data={data}
          manual={manualPagination}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  )
}

SimpleAgGrid.propTypes = {
  title: PropTypes.string,
  className: PropTypes.string,
  onRowClicked: PropTypes.func,
  onRowDataUpdated: PropTypes.func,
  data: PropTypes.array,
  isNarrowList: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired,
      fieldType: PropTypes.oneOf(['date', 'string', 'number', 'currency', 'amount', 'count']),
    }),
  ).isRequired,
  showBorder: PropTypes.bool,
  pagination: PropTypes.bool,
  paginationPageSize: PropTypes.number,
  manualPagination: PropTypes.bool,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  onSortChanged: PropTypes.func,
  localeText: PropTypes.object,
  defaultColDef: PropTypes.object,
  autoFitColumns: PropTypes.bool,
  rowSelection: PropTypes.oneOf([ROW_SELECTION_SINGLE, ROW_SELECTION_MULTIPLE]),
  tooltipShowDelay: PropTypes.number,
  tooltipHideDelay: PropTypes.number,
  suppressRowHoverHighlight: PropTypes.bool,
  disableRightClickContextMenu: PropTypes.bool,
  suppressAggFuncInHeader: PropTypes.bool,
  suppressRowDeselection: PropTypes.bool,
  suppressRowClickSelection: PropTypes.bool,
  onSelectionChanged: PropTypes.func,
  onGridReady: PropTypes.func,
  headerHeight: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.undefined,
  ]),
}

export default SimpleAgGrid
